import { useState, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

// helpers de precisión
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const asNum = (v) => Number.parseFloat(v ?? 0) || 0;

export default function useProductos(
  initial = [],
  initialDescuento = 0,
  catalogo = []
) {
  // 1) Estado de filas
  const [datos, setDatos] = useState(() =>
    initial.length > 0
      ? initial.map((p) => ({ id: uuidv4(), ...p }))
      : [
          {
            id: uuidv4(),
            nombre: "",
            descripcion: "",
            numeroDeParteCliente: "",
            cantidad: 1,
            tarifa: 0,
            iva: 0,
            descuento: 0,
            tipoDescuento: "%",
            importe: 0,
          },
        ]
  );

  // 2) Descuento global
  const [descuento, setDescuento] = useState(initialDescuento);

  // 3) Autocomplete
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [activeRow, setActiveRow] = useState(null);

  // 4) Añadir fila vacía
  const añadirFila = useCallback((e) => {
    e?.preventDefault();
    setDatos((prev) => [
      ...prev,
      {
        id: uuidv4(),
        nombre: "",
        descripcion: "",
        numeroDeParteCliente: "",
        cantidad: 1,
        tarifa: 0,
        iva: 0,
        descuento: 0,
        tipoDescuento: "%",
        importe: 0,
      },
    ]);
  }, []);

  // 5) Eliminar fila
  const eliminar = useCallback((id) => {
    setDatos((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // 6) Actualizar cantidad e importe
  const actualizarCantidad = useCallback((id, cantidad) => {
    const qty = asNum(cantidad);
    setDatos((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const price = asNum(p.tarifa);
        const d = asNum(p.descuento);
        const tipo = p.tipoDescuento || "%";
        const bruto = price * qty;
        const neto =
          tipo === "%" ? bruto * (1 - d / 100) : Math.max(bruto - d, 0);
        return { ...p, cantidad: qty, importe: round2(neto) };
      })
    );
  }, []);

  // 7) Calcular subtotal bruto
  const calcularSubtotal = useCallback(() => {
    const sum = datos.reduce((acc, p) => acc + asNum(p.importe), 0);
    return round2(sum);
  }, [datos]);

  // 8) Tasa IVA
  const obtenerIVA = useCallback(() => {
    if (datos.length > 0 && datos[0].iva) return datos[0].iva;
    return 0.16;
  }, [datos]);

  // 9) Subtotal con descuento global
  const subtotalConDesc = useMemo(() => {
    const sub = calcularSubtotal();
    const d = asNum(descuento); // descuento global en %
    const descMonto = round2(sub * (d / 100));
    return round2(sub - descMonto);
  }, [calcularSubtotal, descuento]);

  // 10) IVA total
  const iva = useMemo(() => {
    return round2(subtotalConDesc * obtenerIVA());
  }, [subtotalConDesc, obtenerIVA]);

  // 11) Total final
  const total = useMemo(() => {
    return round2(subtotalConDesc + iva);
  }, [subtotalConDesc, iva]);

  // 12) Cambio de descuento global
  const manejarCambioDescuento = useCallback((e) => {
    let v = e.target.value;
    if (/^\d*\.?\d*$/.test(v)) {
      let n = parseFloat(v);
      if (isNaN(n)) n = 0;
      if (n > 100) n = 100;
      setDescuento(n);
    }
  }, []);

  // 13) Bulk
  const agregarBulk = useCallback((articulos) => {
    console.log("Agregando artículos a granel:", articulos);
  }, []);

  // 14) Autocomplete y selección
  const handleInputChange = useCallback(
    (e, productoId) => {
      const val = e.target.value;
      // sugerencias
      setFilteredProductos(
        catalogo.filter((prod) =>
          prod.nombre.toLowerCase().includes(val.toLowerCase())
        )
      );
      setActiveRow(productoId);

      const found = catalogo.find(
        (prod) => prod.nombre.toLowerCase() === val.toLowerCase()
      );
      setDatos((prev) => {
        if (found) {
          // evitar duplicados
          const existing = prev.find(
            (i) => i.nombre === found.nombre && i.id !== productoId
          );
          if (existing) {
            // sumar cantidades
            const curr = prev.find((i) => i.id === productoId);
            const sum = (existing.cantidad || 0) + (curr.cantidad || 1);
            return prev
              .map((i) =>
                i.id === existing.id
                  ? {
                      ...i,
                      cantidad: sum,
                      importe: round2(asNum(found.costo) * asNum(sum)),
                    }
                  : i
              )
              .filter((i) => i.id !== productoId);
          }
          return prev.map((i) =>
            i.id === productoId
              ? {
                  ...i,
                  nombre: found.nombre,
                  descripcion: found["Item Desc"] || "",
                  numeroDeParteCliente: found.numeroDeParteCliente,
                  tarifa: found.costo,
                  iva: 0.16,
                  importe: round2(asNum(found.costo) * asNum(i.cantidad)),
                }
              : i
          );
        }
        // texto libre
        return prev.map((i) =>
          i.id === productoId ? { ...i, nombre: val, descripcion: val } : i
        );
      });
    },
    [catalogo]
  );

  return {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    calcularSubtotal,
    subtotalConDesc,
    iva,
    total,
    manejarCambioDescuento,
    obtenerIVA,
    agregarBulk,
    handleInputChange,
    filteredProductos,
    activeRow,
    setDatos,
    setDescuento,
    setFilteredProductos,
  };
}
